import type { IEnrollmentOffer } from "@blockdaemon/bd-sdk";
import { Box, Flex } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  EnrollmentCaughtUpEmptyState,
  EnrollmentSelectActionPrompt,
} from "../../features/enrollment/components/enrollment-empty-state";
import { EnrollmentLoadErrorState } from "../../features/enrollment/components/enrollment-error-state";
import { EnrollmentInboxListPane } from "../../features/enrollment/components/enrollment-inbox-list-pane";
import { EnrollmentInboxLoadingSkeleton } from "../../features/enrollment/components/enrollment-inbox-loading-skeleton";
import { EnrollmentOfferDetail } from "../../features/enrollment/components/enrollment-offer-detail";
import { EnrollmentOfferList } from "../../features/enrollment/components/enrollment-offer-list";
import { usePendingEnrollmentOffers } from "../../features/enrollment/hooks/use-pending-enrollment-offers";
import { useResolvedPartyId } from "../../shared/hooks/use-resolved-party-id";

function EnrollmentInbox({ partyId }: { partyId: string }) {
  const {
    data: offers,
    isLoading,
    error,
    refetch,
    isFetching,
  } = usePendingEnrollmentOffers(partyId);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  const resolvedContractId =
    selectedContractId && offers?.some((offer) => offer.contractId === selectedContractId)
      ? selectedContractId
      : (offers?.[0]?.contractId ?? null);

  const selectedOffer: IEnrollmentOffer | null =
    offers?.find((offer) => offer.contractId === resolvedContractId) ?? null;

  const isCaughtUp = !isLoading && !error && (offers?.length ?? 0) === 0;

  if (isLoading) {
    return <EnrollmentInboxLoadingSkeleton />;
  }

  if (error) {
    return (
      <Flex h="100%" w="100%" direction="column" style={{ flex: 1, minHeight: 0 }}>
        <EnrollmentLoadErrorState
          error={error}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      </Flex>
    );
  }

  if (isCaughtUp) {
    return (
      <Flex h="100%" w="100%" direction="column" style={{ flex: 1, minHeight: 0 }}>
        <EnrollmentCaughtUpEmptyState onRefresh={() => void refetch()} isRefreshing={isFetching} />
      </Flex>
    );
  }

  return (
    <Flex h="100%" w="100%" style={{ minHeight: 0 }}>
      <EnrollmentInboxListPane>
        <EnrollmentOfferList
          offers={offers ?? []}
          selectedContractId={resolvedContractId}
          onSelectOffer={(offer) => setSelectedContractId(offer.contractId)}
        />
      </EnrollmentInboxListPane>

      <Box flex={1} style={{ minWidth: 0, minHeight: 0, overflow: "hidden" }}>
        {selectedOffer ? (
          <EnrollmentOfferDetail key={selectedOffer.contractId} offer={selectedOffer} />
        ) : (offers?.length ?? 0) > 0 ? (
          <EnrollmentSelectActionPrompt />
        ) : null}
      </Box>
    </Flex>
  );
}

function TokenizationRoute() {
  const partyId = useResolvedPartyId();

  return (
    <Box
      h="100%"
      w="100%"
      style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
    >
      <EnrollmentInbox key={partyId} partyId={partyId} />
    </Box>
  );
}

export const Route = createFileRoute("/_layout/tokenization")({
  component: TokenizationRoute,
});
